import { enterAge, settleT } from "../art/craftFeel.ts";
import { UI, withA, type Col } from "../art/pal.ts";
import { ITEMS, type ItemId } from "../data/items.ts";
import { fitText, measureText, wrapPx } from "../engine/font.ts";
import { SCREEN_H, SCREEN_W } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Scene } from "../game/scenes.ts";
import {
  BADGES, DAILY_BONUS, missionDone, missionProgress, missionText, rewardText, streakReward, STREAK_REWARDS,
} from "../game/goals.ts";
import { claimBonus, claimMission } from "../game/goalEvents.ts";
import { backdrop, focus, glass, hint, meter, titleBar } from "../ui/kit.ts";

const TABS = ["DAILY", "BADGES"] as const;
const BADGE_ROWS = 8;
const itemName = (id: ItemId) => ITEMS[id].name;

function untilMidnight(now: number): string {
  const d = new Date(now);
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
  const m = Math.max(0, Math.floor((next - now) / 60000));
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
}

/**
 * GOALS: today's missions with their rewards, the login streak and the daily chest on one tab;
 * every badge, its progress and the title it unlocks on the other.
 */
export function goalsScene(ctx: Ctx): Scene {
  let tab: (typeof TABS)[number] = "DAILY";
  let cursor = 0;
  let scroll = 0;
  let age = 0;
  let flash: { text: string; at: number } | null = null;

  const rows = (): number => (tab === "DAILY" ? (ctx.state.daily?.missions.length ?? 0) + 1 : BADGES.length);

  return {
    id: "goals",
    native: true,
    enter() {
      age = enterAge(ctx.shot);
      tab = "DAILY";
      cursor = scroll = 0;
      flash = null;
      ctx.rollDaily();
    },
    exit() {},
    update() {
      age += ctx.dt;
      const d = ctx.input.menuDir(ctx.now);
      if (d?.x) {
        tab = TABS[(TABS.indexOf(tab) + d.x + TABS.length) % TABS.length]!;
        cursor = scroll = 0;
        ctx.audio.cursor();
        return;
      }
      if (d?.y) {
        cursor = (cursor + d.y + rows()) % rows();
        if (cursor < scroll) scroll = cursor;
        if (cursor >= scroll + BADGE_ROWS) scroll = cursor - BADGE_ROWS + 1;
        ctx.audio.cursor();
      }
      if (ctx.input.consumeAny("Escape", "Tab")) {
        ctx.audio.back();
        ctx.pop();
        return;
      }
      if (!ctx.input.consume("Enter")) return;
      if (tab === "DAILY") {
        const missions = ctx.state.daily?.missions ?? [];
        const got = cursor < missions.length ? claimMission(ctx.state, cursor) : claimBonus(ctx.state);
        if (got) {
          ctx.audio.catchOk();
          flash = { text: got, at: ctx.now };
          ctx.save();
        } else ctx.audio.fail();
        return;
      }
      const b = BADGES[cursor]!;
      if (!ctx.state.badges.includes(b.id)) {
        ctx.audio.fail();
        return;
      }
      ctx.state.title = ctx.state.title === b.title ? "" : b.title;
      ctx.audio.confirm();
      flash = { text: ctx.state.title ? `Now wearing "${ctx.state.title}".` : "Title removed.", at: ctx.now };
      ctx.save();
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const t = settleT(age, reduced, 120);
      backdrop(g, ctx.now, reduced, [30, 60, 90, 255], [8, 10, 24, 255]);
      const daily = ctx.state.daily;
      titleBar(g, "GOALS", daily ? `STREAK ${daily.streak} DAY${daily.streak > 1 ? "S" : ""}` : "", UI.gold);
      let tx = 10;
      for (const name of TABS) {
        const on = name === tab;
        const label = name === "BADGES" ? `BADGES ${ctx.state.badges.length}/${BADGES.length}` : name;
        const w = measureText(label) + 14;
        g.rrect(tx, 26, w, 14, 5, on ? UI.gold : withA(UI.bg2, 220));
        g.text(tx + 7, 29, label, on ? UI.paperInk : UI.textDim);
        tx += w + 4;
      }
      const title = ctx.state.title ? `Title: ${ctx.state.title}` : "No title worn";
      g.textR(374, 29, title, ctx.state.title ? UI.goldHi : UI.textMute);
      const x0 = 8 + Math.round((1 - t) * 20);

      if (tab === "DAILY") {
        if (!daily) {
          glass(g, x0, 46, 368, 60, { r: 8 });
          g.text(x0 + 12, 56, "Missions start with your first day on the road.", UI.textDim);
          return;
        }
        // Streak strip.
        glass(g, x0, 44, 368, 26, { r: 8 });
        const pos = (daily.streak - 1) % STREAK_REWARDS.length;
        for (let i = 0; i < STREAK_REWARDS.length; i++) {
          const cx = x0 + 16 + i * 17;
          const done = i < pos || (i === pos && daily.streakClaimed);
          const col: Col = i === pos ? UI.gold : done ? UI.green : UI.bg3;
          g.disc(cx, 57, i === pos ? 6 : 5, col);
          g.text(cx - 2, 54, `${i + 1}`, i === pos || done ? UI.paperInk : UI.textMute);
        }
        g.text(x0 + 138, 50, `Day ${daily.streak} streak: ${streakReward(daily.streak).label}`, UI.text);
        g.text(x0 + 138, 60, `Tomorrow: ${streakReward(daily.streak + 1).label}`, UI.textMute);

        // Missions.
        daily.missions.forEach((m, i) => {
          const y = 74 + i * 24;
          const on = cursor === i;
          const done = missionDone(ctx.state, m);
          const prog = missionProgress(ctx.state, m);
          g.rrect(x0, y, 368, 22, 6, on ? withA(UI.bg3, 230) : withA(UI.bg1, 200));
          g.rrect(x0 + 3, y + 3, 4, 16, 2, m.claimed ? UI.green : done ? UI.gold : m.live ? UI.pink : UI.teal);
          g.text(x0 + 12, y + 4, fitText(`${on ? ">" : ""}${missionText(m)}`, 170), on ? UI.goldHi : UI.text);
          if (m.live) {
            g.rrect(x0 + 12 + Math.min(170, measureText(missionText(m)) + (on ? 6 : 0)) + 4, y + 3, 30, 9, 3, UI.pink);
            g.text(x0 + 12 + Math.min(170, measureText(missionText(m)) + (on ? 6 : 0)) + 7, y + 4, "LIVE", UI.paperInk);
          }
          meter(g, x0 + 12, y + 14, 120, 4, prog / m.target, { tone: { fill: done ? UI.gold : UI.teal, hi: [230, 250, 255, 255], lo: [30, 80, 90, 255] } });
          g.text(x0 + 138, y + 12, `${prog}/${m.target}`, UI.textDim);
          g.textR(x0 + 300, y + 8, fitText(rewardText(m, itemName), 120), UI.goldHi);
          const tag = m.claimed ? "DONE" : done ? "CLAIM" : "";
          if (tag) {
            const w = measureText(tag) + 10;
            g.rrect(x0 + 360 - w, y + 5, w, 12, 4, m.claimed ? UI.green : UI.gold);
            g.text(x0 + 365 - w, y + 7, tag, UI.paperInk);
          }
          if (on) focus(g, x0 - 1, y - 1, 370, 24, ctx.now, reduced, 7);
        });
        // Daily chest.
        const by = 74 + daily.missions.length * 24 + 2;
        const core = daily.missions.filter((m) => !m.live);
        const ready = core.every((m) => m.claimed);
        const on = cursor === daily.missions.length;
        g.rrect(x0, by, 368, 20, 6, daily.bonusClaimed ? withA(UI.green, 60) : ready ? withA(UI.gold, 80) : withA(UI.bg1, 200));
        g.text(x0 + 12, by + 6, fitText(`${on ? ">" : ""}Daily chest (all ${core.length} missions): ${rewardText(DAILY_BONUS, itemName)}`, 290),
          daily.bonusClaimed ? UI.green : ready ? UI.goldHi : UI.textDim);
        g.textR(x0 + 360, by + 6, daily.bonusClaimed ? "OPENED" : `${core.filter((m) => m.claimed).length}/${core.length}`, UI.text);
        if (on) focus(g, x0 - 1, by - 1, 370, 22, ctx.now, reduced, 7);
        g.textR(374, SCREEN_H - 14, `New missions in ${untilMidnight(Date.now())}`, UI.textMute);
      } else {
        // Badge list.
        glass(g, x0, 44, 180, 150, { r: 8 });
        for (let r = 0; r < BADGE_ROWS; r++) {
          const i = scroll + r;
          const b = BADGES[i];
          if (!b) break;
          const y = 50 + r * 17;
          const on = cursor === i;
          const got = ctx.state.badges.includes(b.id);
          const wearing = got && ctx.state.title === b.title;
          if (on) g.rrect(x0 + 4, y - 2, 172, 16, 5, withA(UI.gold, 50));
          g.disc(x0 + 13, y + 5, 5, got ? UI.gold : UI.bg3);
          g.text(x0 + 11, y + 2, got ? "★" : "·", got ? UI.paperInk : UI.textMute);
          g.text(x0 + 22, y + 2, fitText(b.name, 100), got ? (on ? UI.goldHi : UI.text) : UI.textMute);
          const [have, need] = b.progress(ctx.state);
          g.textR(x0 + 172, y + 2, wearing ? "WORN" : got ? "✓" : `${have}/${need}`, wearing ? UI.green : got ? UI.gold : UI.textMute);
        }
        if (scroll > 0) g.text(x0 + 88, 45, "▲", UI.textDim);
        if (scroll + BADGE_ROWS < BADGES.length) g.text(x0 + 88, 188, "▼", UI.textDim);
        // Detail card.
        const b = BADGES[cursor]!;
        const got = ctx.state.badges.includes(b.id);
        const rx = x0 + 188;
        glass(g, rx, 44, 180, 150, { r: 8, border: got ? UI.gold : UI.line });
        g.disc(rx + 26, 72, 16, got ? UI.gold : UI.bg3);
        g.textBig(rx + 19, 65, got ? "★" : "?", got ? UI.paperInk : UI.textMute, 2);
        g.textShadow(rx + 50, 58, fitText(b.name, 124), got ? UI.goldHi : UI.text, UI.ink);
        g.text(rx + 50, 72, got ? "EARNED" : "LOCKED", got ? UI.green : UI.textMute);
        let y = 96;
        for (const row of wrapPx(b.desc, 160)) { g.text(rx + 10, y, row, UI.text); y += 11; }
        const [have, need] = b.progress(ctx.state);
        meter(g, rx + 10, y + 4, 160, 5, have / need, { tone: { fill: UI.gold, hi: [255, 240, 180, 255], lo: [120, 80, 20, 255] } });
        g.text(rx + 10, y + 13, `${have}/${need}`, UI.textDim);
        g.text(rx + 10, 156, `Title: "${b.title}"`, got ? UI.text : UI.textMute);
        if (b.money) g.text(rx + 10, 168, `Reward: ${b.money} coins`, UI.textMute);
        if (got) hint(g, rx + 10, 180, "A", ctx.state.title === b.title ? "take off title" : "wear title", "a");
      }
      hint(g, 8, SCREEN_H - 16, "◀▶", "tabs", "a");
      if (flash && ctx.now - flash.at < 2600) {
        const w = Math.min(360, measureText(flash.text) + 20);
        glass(g, (SCREEN_W - w) / 2, 8, w, 16, { r: 6, alpha: 235, border: UI.gold });
        g.text((SCREEN_W - w) / 2 + 10, 12, fitText(flash.text, w - 20), UI.goldHi);
      }
    },
  };
}
